/**
 * IL RITMO DELLE LETTURE LIMULO — cioe' quanto spesso una schermata aperta si rilegge da sola.
 *
 * 🔴 **IL REPERTO** (Luca, 2026-09-24 ~14:17Z, dopo un torneo vero su `stg-tma`): *«quando entro
 * in scheda torneo non vedo aumentare in tempo reale il numero di iscritti […] i dati nelle varie
 * schermate si devono aggiornare in automatico»*. E lo screenshot, scattato DOPO la finale, offriva
 * ancora «Ritirati dal torneo» su un torneo `complete`.
 * ⇒ La causa sul lato client: `limuloApi` aveva solo `refetchOnFocus`/`refetchOnReconnect`, e
 * **nessuna** rotta della sezione passava un `pollingInterval`. In una WebView di Telegram
 * `refetchOnFocus` scatta di rado (la WebView non perde il fuoco quando si cambia tab DENTRO la
 * app), quindi una schermata aperta **non si rileggeva mai**. Il realtime di Limulo e' un guscio
 * (ready + heartbeat, nessun evento di dominio: v. il cappello di `limuloApi`), quindi l'unica
 * freschezza possibile e' il polling — e va chiamato polling.
 *
 * ⚠️ Pura e senza `import.meta.env`: la esercita `tools/verify-limulo.ts` sotto Node.
 */
import { nextPollInterval } from '../pollingBackoff.ts';
import { storicoEsitoOf } from './model.ts';

/**
 * 🔑 **12 s sul torneo VIVO** — dettaglio (iscritti, stato, iscrizione), incontri, classifica,
 * iscritti, scheda d'incontro.
 * Il perche' del numero: e' dentro la forchetta 10-15 s chiesta da tma-orc, e l'evento che deve
 * vedere e' umano (un iscritto in piu', un referto depositato dall'avversario), non un orologio di
 * gioco. Sotto i 10 s la scheda farebbe fino a 3-4 richieste ogni giro (torneo + iscrizione + il
 * tab aperto) per un cambiamento che arriva a distanza di minuti; sopra i 15 s «in tempo reale» smette di
 * essere vero per chi guarda il conteggio salire durante un check-in.
 * ⚠️ E' una SCELTA, non una misura: nessuno ha misurato il carico del BFF. Se cambia, cambia qui.
 */
export const LIMULO_POLL_TORNEO_MS = 12_000;

/**
 * 🔑 **30 s sulle LISTE** — elenco tornei (anche nel pannello Eventi della Hub e nella lobby
 * backgammon), «I miei tornei».
 * Una lista cambia di rado (un torneo nuovo, un conteggio) e costa una pagina da 50 righe: la
 * stessa cadenza del primo gradino di `pollingBackoff` — che quindi, sulle liste, **non rallenta
 * al primo fallimento ma solo dal secondo** (`Math.max` in `nextPollInterval`), ed e' voluto.
 */
export const LIMULO_POLL_LISTA_MS = 30_000;

/**
 * 🔑 **15 s sulla «Live» della Hub CR** (I-224): i tornei in corso e i loro incontri non conclusi.
 * Lo stesso ritmo della Live di QDuels (`LiveMatchesPanel`) e della Live backgammon (`QGAMMON_LIVE_POLL_MS`):
 * una partita CR entra e esce dalla lista in pochi minuti, e con 30 s ne mancherebbe mezzo giro.
 * ⚠️ Costo: 1 + T letture ogni 15 s per telefono aperto (T = tornei CR in corso; 0 su staging il
 * 2026-09-27 00:17Z [M qtournaments-orcb]). La lettura unica di kb#299/kb#300 lo porta a una.
 */
export const LIMULO_POLL_LIVE_MS = 15_000;

/**
 * 🔑 **60 s sulle SFIDE DI PIATTAFORMA in arrivo** (`GET /v1/challenges?direction=in&state=pending`,
 * T2-5, lotto B1): la barra delle azioni le legge da QUALUNQUE schermata, e una sfida vale 24 h — non
 * e' un evento da secondi. 60 s e' la cadenza che tma-orcr (proprietaria della barra) ha accettato
 * il 29/9 00:45Z, con le condizioni: si ferma fuori serratura, senza sessione e a pagina nascosta,
 * e al ritorno rilegge UNA volta (`refetchOnFocus`). Dopo Accetta/Rifiuta la riga non aspetta il
 * giro: la toglie il tocco. ⚠️ E' una SCELTA, non una misura di carico.
 */
export const LIMULO_POLL_SFIDE_MS = 60_000;

/**
 * Il torneo e' in uno stato TERMINALE? — concluso, annullato/abbandonato, archiviato.
 *
 * 🔴 **UNA SOLA ENUMERAZIONE: quella dello storico** (`storicoEsitoOf` in `model.ts`, che copre i
 * tre vocabolari del motore — interno, pubblico e legacy: `complete`/`completed`, `canceled`/
 * `cancelled`/`abandoned`, `archived`). Una seconda lista qui divergerebbe dalla prima alla prima
 * voce aggiunta, e la schermata direbbe «concluso» dove lo storico dice «sconosciuto».
 * [M 2026-09-24 su `qlash-limulo@origin/staging` = `34c7496`: `TOURNAMENT_STATES` in
 * `persistenza/tournament.model.js` ha 14 stati, e i quattro dopo i quali «un torneo non impegna
 * piu' nessuno» sono `STATI_CONCLUSI` in `core/presenze.js:188` — gli stessi.]
 *
 * ⚠️ `false` su uno stato che non riconosciamo, o assente: **«non so» non e' «finito»**. Il verso
 * del ripiego e' deliberato — un torneo creduto vivo si rilegge una volta di troppo; uno creduto
 * finito smette di aggiornarsi e nasconde le azioni a chi poteva ancora farle.
 */
export function isTournamentTerminal(tournament: unknown): boolean {
  return storicoEsitoOf(tournament) !== 'sconosciuto';
}

/**
 * Il ritmo di base delle letture legate a UN torneo, dato il torneo (o `undefined` se non e'
 * ancora arrivato).
 *
 * ⛔ **Zero su un torneo terminale**: niente cambia piu', e rileggerlo ogni 12 s e' traffico e
 * batteria spesi per niente — su TUTTE le schermate che restano aperte su un torneo finito.
 * ⚠️ `undefined` (non arrivato, o la lettura e' fallita) ⇒ **vivo**: v. `isTournamentTerminal`.
 * E con la lettura fallita ci pensa il backoff a non insistere.
 */
export function limuloTournamentPollBase(tournament: unknown): number {
  return isTournamentTerminal(tournament) ? 0 : LIMULO_POLL_TORNEO_MS;
}

/**
 * L'intervallo da passare a RTK Query adesso, dato il ritmo di base e i fallimenti consecutivi.
 *
 * 🔴 **La guardia sullo zero e' il motivo per cui questa funzione esiste.** `nextPollInterval(0,
 * n)` con `n > 0` risponde il gradino del backoff (30 s…) — cioe' **accenderebbe** il polling
 * su un torneo terminale al primo fallimento. «Rallentare» non deve mai voler dire «ricominciare».
 */
export function limuloPollInterval(base: number, failureStreak: number): number {
  if (base <= 0) return 0;
  return nextPollInterval(base, failureStreak);
}

/**
 * Il ritmo della scheda di UN incontro: quello del torneo, finche' l'incontro puo' ancora cambiare.
 *
 * ⚠️ **Zero quando l'incontro e' chiuso** — `decided` (referto definitivo col vincitore) o `bye`
 * (turno passato senza avversario): li' non arriva piu' niente da aspettare. Tutti gli altri stati
 * (`pending`, `proposed`, `disputed`, `reported`, `incomplete`) sono attese di qualcun altro — un
 * referto dell'avversario, una conferma, la decisione dello staff, una parte che arriva — ed e'
 * esattamente cio' che chi tiene aperta la scheda sta aspettando.
 * ⚠️ `null` = l'incontro non e' arrivato: si segue il torneo (e il backoff, se la lettura fallisce).
 *
 * @param stato l'esito di `matchStateOf` (`lib/limulo/match.ts`), passato come stringa per non
 *   legare questo file alla forma dell'incontro.
 */
export function limuloMatchPollBase(stato: string | null, tournament: unknown): number {
  if (stato === 'decided' || stato === 'bye') return 0;
  return limuloTournamentPollBase(tournament);
}
